/**
 * The tape itself (DOP-627): the run's first rows, each column header saying
 * where the column came from — Mapped, Derived or Unmapped — and
 * opening the column panel on a click.
 *
 * The header is a real button, so the "click any column" affordance is
 * reachable by keyboard; the chat's preview card used a clickable `<th>`.
 */

import React from "react";
import type { LoanTapePreviewColumn } from "../Chat/Chat.types";
import {
	ORIGIN_DOT,
	ORIGIN_LABEL,
	ORIGIN_TEXT,
	columnOrigin,
} from "./loanTapeColumns";

export interface TapeTableProps {
	columns: readonly LoanTapePreviewColumn[];
	rows: readonly string[][];
	/** The header of the column the panel is open on. */
	selected: string | null;
	onSelect: (column: LoanTapePreviewColumn) => void;
}

const TapeTable: React.FC<TapeTableProps> = ({
	columns,
	rows,
	selected,
	onSelect,
}) => (
	<div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
		<table className="w-full border-collapse text-[13px] tabular-nums">
			<thead>
				<tr>
					{columns.map((column) => {
						const origin = columnOrigin(column);
						const isSelected = column.header === selected;
						const right = column.align === "right";
						return (
							<th
								key={column.header}
								scope="col"
								className={`whitespace-nowrap border-b border-gray-200 p-0 align-top font-normal ${
									isSelected
										? "bg-[#eef4ff] shadow-[inset_0_-2px_0_#444ce7]"
										: origin === "unmapped"
											? "bg-[#fffaeb]"
											: "bg-[#fafafa]"
								}`}
							>
								<button
									type="button"
									onClick={() => onSelect(column)}
									aria-pressed={isSelected}
									className={`flex w-full flex-col gap-1 px-3 py-2 hover:bg-black/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#008782] ${
										right ? "items-end text-right" : "items-start text-left"
									}`}
								>
									<span className="text-[12.5px] font-semibold text-[#1F2937]">
										{column.header}
									</span>
									<span
										className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${ORIGIN_TEXT[origin]}`}
									>
										<span
											aria-hidden
											className={`inline-block h-[7px] w-[7px] rounded-full ${ORIGIN_DOT[origin]}`}
										/>
										{ORIGIN_LABEL[origin]}
									</span>
								</button>
							</th>
						);
					})}
				</tr>
			</thead>
			<tbody>
				{rows.map((row, rowIndex) => (
					// Preview rows have no id of their own; their order is the sheet's.
					// eslint-disable-next-line react/no-array-index-key
					<tr key={rowIndex}>
						{columns.map((column, columnIndex) => {
							const isSelected = column.header === selected;
							return (
								<td
									key={column.header}
									className={`whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 text-[#1F2937] ${
										column.align === "right" ? "text-right" : "text-left"
									} ${
										isSelected
											? "bg-[#f5f8ff] font-semibold"
											: columnOrigin(column) === "unmapped"
												? "bg-[#fffdf7]"
												: ""
									}`}
								>
									{row[columnIndex] ?? ""}
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

export default TapeTable;
